import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	Button,
	Card,
	CardBody,
	Flex,
	Skeleton,
	Spinner,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Text,
	useDisclosure,
} from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import AcceptWithReasonModal from "../components/modals/AcceptWithReasonModal";
import { useMutation, useQueryClient } from "react-query";
import MoonSteroidIcon from "../components/icons/MoonSteroidIcon";
import {
	useUser,
	useUserCentersPaginated,
	useUserStats,
} from "../hooks/user.hooks";
import ContactIcon from "../components/icons/ContactIcon";
import { updateUser } from "../mutations/user.mutations";
import DataTable from "../components/table/ServerSideTable";
import type { ColumnDef } from "@tanstack/react-table";
import type { User } from "../types/user.types";
import type { UserCenter } from "../types/center.types";
import { format } from "date-fns";
import i18n from "../i18n";
import PersistingTabIndex from "../components/PersistingTabIndex";
import UserModal from "../components/modals/UserModal";
import { fPhoneNumber } from "../lib/format";

const ContactUsersDetailPageTable = ({ id }: Partial<User>) => {
	const columns = useMemo<ColumnDef<UserCenter>[]>(
		() => [
			{
				accessorFn: (row) => row.name,
				id: "name",
				header: "Nom",
				cell: (info) => info.getValue(),
			},
			{
				accessorFn: (row) =>
					row.userJoinedAt ? format(row.userJoinedAt, "dd/MM/yyyy") : "N/A",
				id: "date",
				header: "Date d'ajout au centre",
				cell: (info) => info.getValue(),
			},
			{
				accessorFn: (row) => (
					<Text textTransform={"capitalize"}>{i18n.t(row.role)}</Text>
				),
				id: "role",
				header: "Rôle",
				cell: (info) => info.getValue(),
			},
			{
				accessorFn: (row) => (
					<Text textTransform={"capitalize"}>{i18n.t(row.access)}</Text>
				),
				id: "access",
				header: "Accès",
				cell: (info) => info.getValue(),
			},
			{
				accessorFn: () => <></>,
				id: "actions",
				header: "",
				cell: (info) => info.getValue(),
			},
		],
		[],
	);
	return (
		<DataTable
			columns={columns}
			paginatedQuery={useUserCentersPaginated({ id })}
			w={["12rem", "14rem", "14rem", "auto", "14rem"]}
			flexGrow={[0, 0, 0, 1, 0]}
			cellLink={(cell) => {
				return `/contacts/centers/${cell.row.id}`;
			}}
		></DataTable>
	);
};

function ContactCentersDetailPage() {
	const { id } = useParams();
	const { data: user, isLoading: centerLoading } = useUser(id);
	const { data: userStats, isLoading: userStatsLoading } = useUserStats(id);
	const [tabIndex, setTabIndex] = useState<number>(0);
	const userUpdateModal = useDisclosure();
	const sleepModal = useDisclosure();
	const queryClient = useQueryClient();
	const { mutate: mutateUpdateUser } = useMutation(updateUser, {
		onSuccess: () => {
			queryClient.invalidateQueries("user");
		},
	});
	const toggleSleepMode = () => {
		mutateUpdateUser({
			id: id ? Number.parseInt(id) : undefined,
			activatedAt: user?.activatedAt ? null : new Date(),
		});
	};

	if (centerLoading) {
		return <Spinner></Spinner>;
	}
	if (user === null || user === undefined) {
		return <></>;
	}
	return (
		<>
			<Flex justifyContent={"space-between"}>
				<Flex direction={"column"}>
					<Breadcrumb
						spacing="8px"
						separator={<ChevronRightIcon color="gray.500" />}
					>
						<BreadcrumbItem>
							<BreadcrumbLink
								href="/contacts"
								color="gray.600"
								fontWeight={600}
								fontSize="14px"
							>
								Contact
							</BreadcrumbLink>
						</BreadcrumbItem>

						<BreadcrumbItem>
							<BreadcrumbLink
								href="#"
								color="brand.700"
								fontWeight={600}
								fontSize="14px"
							>
								<Text textTransform={"capitalize"}>
									{user.firstName} {user.lastName}
								</Text>
							</BreadcrumbLink>
						</BreadcrumbItem>
					</Breadcrumb>

					<PageTitle>
						{user.firstName} {user.lastName}
					</PageTitle>

					{/* STATS */}
					<Flex gap={4} mt="2rem">
						<Flex direction={"column"} w={"10rem"}>
							<Text
								lineHeight="15px"
								fontSize="10px"
								fontWeight="700"
								color="gray.400"
							>
								Commandes
							</Text>
							{!userStatsLoading && (
								<Text
									lineHeight="36px"
									fontSize="24px"
									fontWeight="600"
									color="gray.950"
								>
									{userStats?.commandsCount ?? "---"}
								</Text>
							)}
							{userStatsLoading && <Skeleton>--</Skeleton>}
						</Flex>

						<Flex direction={"column"} w={"10rem"}>
							<Text
								lineHeight="15px"
								fontSize="10px"
								fontWeight="700"
								color="gray.400"
							>
								Centres
							</Text>
							{!userStatsLoading && (
								<Text
									lineHeight="36px"
									fontSize="24px"
									fontWeight="600"
									color="gray.950"
								>
									{userStats?.centersCount ?? "---"}
								</Text>
							)}
							{userStatsLoading && <Skeleton>--</Skeleton>}
						</Flex>
					</Flex>
				</Flex>
				{user.activatedAt === null && (
					<Flex
						backgroundColor={"brand.50"}
						direction={"column"}
						alignItems={"left"}
						p={4}
						borderRadius="xl"
						border="solid 1px"
						borderColor="gray.300"
						w={"400px"}
					>
						<MoonSteroidIcon h={"38px"} w={"38px"} color={"brand.700"} />

						<Flex direction={"column"} mt={1}>
							<Text
								lineHeight="21px"
								fontSize="14px"
								fontWeight="600"
								color="gray.700"
							>
								Compte désactivé
							</Text>
							<Text
								lineHeight="21px"
								fontSize="14px"
								fontWeight="400"
								color="gray.600"
							>
								L'utilisateur ne peut actuellement pas se connecter.
							</Text>
						</Flex>

						<Button
							color={"brand.700"}
							variant="link"
							w="fit-content"
							mt={4}
							onClick={toggleSleepMode}
						>
							Activer
						</Button>
					</Flex>
				)}
			</Flex>

			{/* TABS */}
			<PersistingTabIndex
				id="users-tabs"
				variant="line"
				index={tabIndex}
				onChange={setTabIndex}
			>
				<TabList>
					<Tab _selected={{ color: "brand.700", borderColor: "brand.700" }}>
						Informations
					</Tab>
					<Tab _selected={{ color: "brand.700", borderColor: "brand.700" }}>
						Centres rattachés
					</Tab>
				</TabList>

				<TabPanels>
					<TabPanel px={0}>
						<Card mt={4}>
							<CardBody>
								<Flex
									my="12px"
									mx="4px"
									py="4px"
									borderBottom="1px"
									borderColor="gray.100"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>Nom</Text>
									<Text textTransform={"capitalize"}>
										{user.firstName} {user.lastName}
									</Text>
								</Flex>

								<Flex
									my="12px"
									mx="4px"
									py="4px"
									borderBottom="1px"
									borderColor="gray.100"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>Téléphone</Text>
									<Text>{fPhoneNumber(user.phoneNumber ?? "")}</Text>
								</Flex>

								<Flex
									my="12px"
									mx="4px"
									py="4px"
									borderBottom="1px"
									borderColor="gray.100"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>Email</Text>
									<Text>{user.email}</Text>
								</Flex>

								<Flex
									my="12px"
									mx="4px"
									py="4px"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>Mot de passe</Text>
									<Text>........</Text>
								</Flex>

								<Flex
									my="12px"
									mx="4px"
									py="4px"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>Date de dernière connexion</Text>
									<Text>
										{user.lastConnectionAt
											? format(user.lastConnectionAt, "HH:mm dd/MM/yyyy")
											: "Ne s'est jamais connecté"}
									</Text>
								</Flex>

								<Flex w="100%" justifyContent={"end"}>
									<Button onClick={userUpdateModal.onOpen}>Modifier</Button>
									<UserModal
										isOpen={userUpdateModal.isOpen}
										onClose={userUpdateModal.onClose}
										user={user}
									/>
								</Flex>
							</CardBody>
						</Card>

						{user.activatedAt !== null && (
							<Flex mt={4}>
								<Button onClick={sleepModal.onOpen}>Désactivation</Button>
								<AcceptWithReasonModal
									title={"Désactivation du compte"}
									icon={<ContactIcon color="red.700" />}
									description={<></>}
									discardButtonText="Annuler"
									validationButtonText="Désactivation"
									colorScheme="red"
									isOpen={sleepModal.isOpen}
									onClose={sleepModal.onClose}
									onValidation={toggleSleepMode}
								></AcceptWithReasonModal>
							</Flex>
						)}
					</TabPanel>

					<TabPanel px={0}>
						<ContactUsersDetailPageTable
							id={id ? Number.parseInt(id) : undefined}
						/>
					</TabPanel>
				</TabPanels>
			</PersistingTabIndex>
		</>
	);
}

export default ContactCentersDetailPage;
